import type { EmployeeScheduleRuleDto } from '@crv/contracts/time-tracking';

import { parseDateTimeInputValue } from '../timezones/timezone-utils.js';

const DAY_MS = 24 * 60 * 60 * 1000;

interface LocalDateParts {
  year: number;
  month: number;
  day: number;
  dayOfWeek: number;
  isoDate: string;
}

export interface ScheduleWindow {
  rule: EmployeeScheduleRuleDto;
  localDate: string;
  startAt: Date;
  endAt: Date;
}

export interface SchedulePrompt {
  type: 'start' | 'end';
  window: ScheduleWindow;
}

export function resolveSchedulePrompt(
  rules: EmployeeScheduleRuleDto[],
  now: Date,
  timeZone: string,
  hasOpenEntry: boolean,
): SchedulePrompt | null {
  const windows = resolveScheduleWindowsForNow(rules, now, timeZone);
  if (windows.length === 0) {
    return null;
  }

  const activeWindow = windows.find((window) => now >= window.startAt && now <= window.endAt) ?? null;
  const latestWindow =
    windows
      .filter((window) => window.startAt <= now)
      .sort((a, b) => b.startAt.getTime() - a.startAt.getTime())
      .at(0) ?? null;

  if (hasOpenEntry) {
    if (latestWindow && now >= latestWindow.endAt) {
      return { type: 'end', window: latestWindow };
    }
    return null;
  }

  if (activeWindow && now >= activeWindow.startAt) {
    return { type: 'start', window: activeWindow };
  }

  return null;
}

export function resolveScheduleWindowsForNow(
  rules: EmployeeScheduleRuleDto[],
  now: Date,
  timeZone: string,
): ScheduleWindow[] {
  const todayParts = getLocalDateParts(now, timeZone);
  const todayDate = new Date(Date.UTC(todayParts.year, todayParts.month - 1, todayParts.day));
  const yesterdayDate = new Date(todayDate.getTime() - DAY_MS);
  const yesterdayParts = getLocalDateParts(yesterdayDate, timeZone);

  const windows = [
    ...resolveScheduleWindowsForDate(rules, todayParts, timeZone),
    ...resolveScheduleWindowsForDate(rules, yesterdayParts, timeZone),
  ];

  return windows.sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
}

function resolveScheduleWindowsForDate(
  rules: EmployeeScheduleRuleDto[],
  localDate: LocalDateParts,
  timeZone: string,
): ScheduleWindow[] {
  const dateUtc = new Date(Date.UTC(localDate.year, localDate.month - 1, localDate.day));

  return rules
    .filter((rule) => rule.dayOfWeek === localDate.dayOfWeek)
    .filter((rule) => {
      const effectiveFrom = parseIsoDate(rule.effectiveFrom);
      if (!effectiveFrom || effectiveFrom.getTime() > dateUtc.getTime()) {
        return false;
      }
      const effectiveTo = parseIsoDate(rule.effectiveTo);
      if (effectiveTo && effectiveTo.getTime() < dateUtc.getTime()) {
        return false;
      }
      return true;
    })
    .map((rule) => buildScheduleWindow(rule, localDate.isoDate, timeZone))
    .filter((window): window is ScheduleWindow => window !== null);
}

function buildScheduleWindow(
  rule: EmployeeScheduleRuleDto,
  localDate: string,
  timeZone: string,
): ScheduleWindow | null {
  const startMinutes = toMinutes(rule.startLocalTime);
  const endMinutes = toMinutes(rule.endLocalTime);
  const endDate = endMinutes <= startMinutes ? addDays(localDate, 1) : localDate;

  const startAt = parseDateTimeInputValue(`${localDate}T${rule.startLocalTime}`, timeZone);
  const endAt = parseDateTimeInputValue(`${endDate}T${rule.endLocalTime}`, timeZone);

  if (!startAt || !endAt) {
    return null;
  }

  return { rule, localDate, startAt, endAt };
}

function getLocalDateParts(date: Date, timeZone: string): LocalDateParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const getPart = (type: Intl.DateTimeFormatPartTypes): string => {
    const part = parts.find((candidate) => candidate.type === type)?.value;
    if (!part) {
      throw new Error(`Missing Intl.DateTimeFormat part: ${type}`);
    }
    return part;
  };

  const year = Number(getPart('year'));
  const month = Number(getPart('month'));
  const day = Number(getPart('day'));
  const isoDate = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const localDate = new Date(Date.UTC(year, month - 1, day));

  return {
    year,
    month,
    day,
    isoDate,
    dayOfWeek: localDate.getUTCDay(),
  };
}

function parseIsoDate(value: string | null): Date | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map((part) => Number(part));
  return hours * 60 + minutes;
}

function addDays(isoDate: string, days: number): string {
  const parsed = new Date(`${isoDate}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }

  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}
